import { useCallback, useEffect, useMemo, useState } from 'react';
import checkArray from '@/utilites/check-array';
import { getMarketValueForToday } from '@/utilites/market-value';
import { startOfDay } from 'date-fns';
import useTrendingPercent from '@/hooks/useTrendingPercent';
import { LastActivity } from '@/types/other';
import { lastActivityType } from '@/constants/others';
import { AskMarketValue, BidMarketValue } from '@/types/utilities';
import { useQueries } from '@tanstack/react-query';
import getSkuOrders from '@/api/getSkuOrders';
import getSimilarProducts from '@/api/getSimilarProducts';
import { getHighestBid } from '@/utilites/sku-operations';
import { v4 } from 'uuid';

export default function useSkuPage(
  asks: AskMarketValue[],
  bids: BidMarketValue[],
  skuRetailPrice: number,
  slug: string,
  userId: string | null,
  spiritoryBottle: boolean = false
) {
  const [filteredBid, setFilteredBid] = useState<number | null>(null);
  const [filteredAsk, setFilteredAsk] = useState<number | null>(null);
  const highestBid = useMemo(() => getHighestBid(bids, userId), [bids, userId]);

  const [ordersQuery, similarProductsQuery] = useQueries({
    queries: [
      {
        queryKey: ['sku', slug, 'orders'],
        queryFn: ({ queryKey }) => getSkuOrders(queryKey[1] as string),
        enabled: !!slug,
      },
      {
        queryKey: ['similarProducts', slug],
        queryFn: ({ queryKey }) => getSimilarProducts(queryKey[1] as string),
        enabled: !!slug,
      },
    ],
  });

  const orders = useMemo(
    () => ordersQuery.data?.data || [],
    [ordersQuery.data]
  );

  const lastActivities: LastActivity[] = useMemo(() => {
    const sortedOrders = orders
      .sort(
        (a, b) =>
          new Date(b.skuOrderStatus.paid).getTime() -
          new Date(a.skuOrderStatus.paid).getTime()
      )
      .map((order) => {
        return {
          id: v4(),
          type: lastActivityType.order as 'order',
          created: order.skuOrderStatus.paid,
          value: order.payValue,
        };
      });
    const sortedAsks = asks
      .sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      )
      .map((ask) => {
        return {
          id: v4(),
          type: lastActivityType.ask as 'ask',
          created: ask.created,
          value: ask.value,
        };
      });
    const sortedBids = bids
      .sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      )
      .map((bid) => {
        return {
          id: v4(),
          type: lastActivityType.bid as 'bid',
          created: bid.created,
          value: bid.value,
        };
      });

    const sortedItems = [...sortedOrders, ...sortedAsks, ...sortedBids]
      .sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      )
      .slice(0, 7);

    return sortedItems;
  }, [asks, bids, orders]);

  const skuCurrentMarketValue = useMemo(() => {
    return (
      getMarketValueForToday(
        asks,
        bids,
        orders,
        skuRetailPrice,
        startOfDay(new Date())
      ) ?? 0
    );
  }, [asks, bids, orders, skuRetailPrice]);

  const skuTotalReturn = useMemo(() => {
    if (skuRetailPrice > 0) {
      return skuCurrentMarketValue - skuRetailPrice;
    }
    return 0;
  }, [skuCurrentMarketValue, skuRetailPrice]);

  const skuTotalReturnPercentage = useMemo(() => {
    if (skuRetailPrice > 0) {
      return Number(((skuTotalReturn / skuRetailPrice) * 100).toFixed(2));
    }
    return 0;
  }, [skuTotalReturn, skuRetailPrice]);

  const skuAverageSalePrice = useMemo(() => {
    const skuOrdersLength = orders?.length || 0;
    if (checkArray(orders)) {
      const totalOrdersPrices = orders.reduce(
        (total: number, order) => total + order.payValue,
        0
      );
      return totalOrdersPrices / skuOrdersLength;
    }
    return 0;
  }, [orders]);

  const trendingPercent = useTrendingPercent(orders, skuRetailPrice);

  const filteredBidsCallback = useCallback(
    (userIdParam: string | null) => {
      const fa = bids.filter((bid) => {
        if (Number(bid.ownerUserId) !== Number(userIdParam) || !userIdParam)
          return bid;
      });
      const values = fa.map((object) => {
        return object.value;
      });
      return values.length ? Math.max(...values) : null;
    },
    [bids]
  );
  const filteredAsksCallback = useCallback(
    (userIdParam: string | null) => {
      const fa = asks.filter((ask) => {
        if (Number(ask.ownerUserId) !== Number(userIdParam) || !userIdParam)
          return ask;
      });
      const values = fa.map((object) => {
        if (spiritoryBottle) {
          return object.value - object.shippingPrice;
        }
        return object.value;
      });
      return values.length ? Math.min(...values) : null;
    },
    [asks, spiritoryBottle]
  );

  const memoizedFilteredBid = useMemo(
    () => filteredBidsCallback(userId),
    [userId, filteredBidsCallback]
  );
  const memoizedFilteredAsk = useMemo(
    () => filteredAsksCallback(userId),
    [userId, filteredAsksCallback]
  );

  useEffect(() => {
    if (memoizedFilteredBid !== filteredBid)
      setFilteredBid(memoizedFilteredBid);
    if (memoizedFilteredAsk !== filteredAsk)
      setFilteredAsk(memoizedFilteredAsk);
  }, [memoizedFilteredBid, memoizedFilteredAsk, filteredBid, filteredAsk]);

  return {
    lastActivities,
    skuCurrentMarketValue,
    skuTotalReturn,
    skuAverageSalePrice,
    trendingPercent,
    orders,
    similarProductsQuery,
    ordersQuery,
    highestBid,
    filteredBid,
    filteredAsk,
    memoizedFilteredBid: memoizedFilteredBid ?? 0,
    memoizedFilteredAsk: memoizedFilteredAsk ?? 0,
    skuTotalReturnPercentage,
  };
}
